import { Box, BoxProps, ProgressBar, Text, useStyle } from '@betty/moonlight';
import { memo, useEffect, useRef } from 'react';
import useLoyalty from './hooks/useLoyalty';
import { useTranslation } from '@betty/i18n';
import { StackNavigationProp, useNavigation } from '@betty/navigation';
import { Pressable } from 'react-native';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated';

const staticStyles: BoxProps = {
  height: 40,
  variant: 'default',
  width: '100%',
  backgroundColor: 'secondaryBackgroundDefault',
  borderRadius: 'radius-200',
  padding: 'space-200',
  gap: 'space-100',
  flexDirection: 'column',
  justifyContent: 'center',
  overflow: 'visible',
  zIndex: 1,
};

function Levels(props: BoxProps) {
  const pressableStyle = useStyle({ ...staticStyles, ...props });
  const [t] = useTranslation('levels');

  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const handleClick = () => {
    navigation.navigate('MiscScreen', { path: '/level-up' });
  };

  const { loyalty, points } = useLoyalty();
  const prevLevel = useRef(loyalty?.level);
  const opacityValue = useSharedValue(0);

  useEffect(() => {
    if (loyalty?.level !== prevLevel.current) {
      prevLevel.current = loyalty?.level;

      opacityValue.value = withSequence(
        withTiming(1, { duration: 400 }),
        withTiming(1, { duration: 3200 }),
        withTiming(0, { duration: 400 }),
      );
    }
  }, [loyalty?.level, opacityValue, prevLevel]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacityValue.value,
      transform: [
        {
          translateY: withSequence(
            withTiming(-5, { duration: 400, easing: Easing.out(Easing.ease) }),
            withTiming(-5, { duration: 3200 }),
            withTiming(0, { duration: 400, easing: Easing.in(Easing.ease) }),
          ),
        },
      ],
    };
  });

  return (
    <Pressable style={pressableStyle} onPress={handleClick}>
      {loyalty ? (
        <Box gap="space-100">
          <Text variant="nav-label" color={'darkSurfacePrimary'}>
            {t('level')} {loyalty.level}
          </Text>

          <ProgressBar
            level={loyalty.level}
            from={loyalty.startPoints}
            to={loyalty.endPoints}
            progress={points}
          />
        </Box>
      ) : null}

      {prevLevel.current && loyalty?.level !== prevLevel.current ? (
        <Box
          key={loyalty?.level}
          position="absolute"
          minWidth={70}
          alignItems="center"
          top="space-0"
          variant="gradient"
          gradient="accentBackground"
          padding="space-200"
          borderRadius="radius-100"
          alignSelf="center"
          animatedStyle={animatedStyle}
        >
          <Text variant="nav-label" locNS="levels" locKey="levelUp" />
        </Box>
      ) : null}
    </Pressable>
  );
}

export default memo(Levels);
